import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';

import {
  canSaveAllDirty,
  discardDirtyCards,
  flushAutosavingDirtyCards,
  flushDirtyCards,
  hasDirtyCards,
  hasDirtyCardsNeedingPrompt,
  summarizeDirtyCardsNeedingPrompt,
  type DirtyCardSummary,
} from '@/features/patient-record/lib/dirtyRegistry';

export interface UnsavedNavGuard {
  /** True while something is held pending the user's Save / Discard / Keep-editing choice. */
  readonly blocked: boolean;
  /** Whether every unsaved draft can currently be committed (false ⇒ a required field is empty). */
  readonly canSave: boolean;
  /** What is unsaved (label + changed field NAMES), for the confirm dialog. */
  readonly summaries: readonly DirtyCardSummary[];
  /** Commit everything unsaved in scope, then let the held action through. No-op when !canSave. */
  readonly onSave: () => void;
  /** Drop everything unsaved in scope, then let the held action through. */
  readonly onDiscard: () => void;
  /** Cancel the held action and return the user to their edit. */
  readonly onKeepEditing: () => void;
  /**
   * Run a non-navigation action that would destroy unsaved work (e.g. a view-mode toggle that
   * collapses and unmounts an open form). If nothing is unsaved it runs immediately; otherwise it is
   * held behind the same Save / Discard / Keep-editing prompt.
   */
  readonly guard: (action: () => void) => void;
}

/**
 * PTV2-1301 — protect unsaved work on the patient record from three exits: switching section
 * (router navigation), a destructive same-page action like a view-mode toggle (which collapses and
 * unmounts open forms), and a full page unload (refresh / close tab).
 *
 * Router navigation is held with {@link useBlocker}; same-page destructive actions go through
 * {@link UnsavedNavGuard.guard}; page unload is caught with a `beforeunload` handler (native
 * browser prompt — the platform gives no custom UI there). All three resolve through the one shared
 * confirm dialog the caller renders from this hook's state.
 *
 * `scopeId` defaults to `null` — the patient-record page's own cards and inline forms. Pedigree
 * node-editor edits register under their person guid and are handled by the pedigree's own
 * node-switch flush, so they are intentionally out of this guard's scope.
 */
export function useUnsavedNavGuard(scopeId: string | null = null): UnsavedNavGuard {
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      const moving =
        currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.hash !== nextLocation.hash;
      if (!moving || !hasDirtyCards(scopeId)) return false;
      // PTV2-1327: save the AUTOSAVING surfaces NOW — commit their pending debounce before we navigate
      // away and unmount them, so nothing is lost (we don't wait for the timer). Only BLOCK for
      // surfaces that still need a decision (create/add forms, modals, or anything with autosave off).
      flushAutosavingDirtyCards(scopeId);
      return hasDirtyCardsNeedingPrompt(scopeId);
    },
    [scopeId],
  );
  const blocker = useBlocker(shouldBlock);

  // A same-page destructive action held pending the user's choice (view-mode toggle, etc.).
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Native unload prompt on refresh / tab close while there is unsaved work. The browser shows its
  // own generic dialog; returnValue must be set for the prompt to appear.
  const scopeRef = useRef(scopeId);
  scopeRef.current = scopeId;
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!hasDirtyCards(scopeRef.current)) return;
      // Best-effort: fire the autosaving commits before unload (they may not finish, but not waiting
      // for the debounce gives them the best chance). Only raise the native prompt for surfaces that
      // still need a decision.
      flushAutosavingDirtyCards(scopeRef.current);
      if (hasDirtyCardsNeedingPrompt(scopeRef.current)) {
        event.preventDefault();
        // Deprecated but still required for the native prompt to appear in Safari and older
        // engines; preventDefault() alone covers only current Chromium/Firefox.
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => { window.removeEventListener('beforeunload', onBeforeUnload); };
  }, []);

  const blocked = blocker.state === 'blocked' || pendingAction !== null;

  const resolveProceed = useCallback(() => {
    if (blocker.state === 'blocked') blocker.proceed();
    if (pendingAction !== null) {
      pendingAction();
      setPendingAction(null);
    }
  }, [blocker, pendingAction]);

  const resolveCancel = useCallback(() => {
    if (blocker.state === 'blocked') blocker.reset();
    setPendingAction(null);
  }, [blocker]);

  const onSave = useCallback(() => {
    // Guarded by canSave at the button, but re-check: a no-op save followed by proceed would lose
    // the draft. Fire-and-forget: each save is optimistic and settles on its own.
    if (!canSaveAllDirty(scopeId)) return;
    flushDirtyCards({ scopeId });
    resolveProceed();
  }, [scopeId, resolveProceed]);

  const onDiscard = useCallback(() => {
    discardDirtyCards({ scopeId });
    resolveProceed();
  }, [scopeId, resolveProceed]);

  const onKeepEditing = useCallback(() => {
    resolveCancel();
  }, [resolveCancel]);

  const guard = useCallback(
    (action: () => void) => {
      if (!hasDirtyCards(scopeId)) {
        action();
        return;
      }
      // Same partition as navigation: commit the autosaving surfaces now; only hold the action behind
      // the prompt when a non-autosaving surface still needs a decision.
      flushAutosavingDirtyCards(scopeId);
      if (hasDirtyCardsNeedingPrompt(scopeId)) {
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [scopeId],
  );

  return {
    blocked,
    canSave: blocked ? canSaveAllDirty(scopeId) : true,
    summaries: blocked ? summarizeDirtyCardsNeedingPrompt(scopeId) : [],
    onSave,
    onDiscard,
    onKeepEditing,
    guard,
  };
}
